import React, { useState, useEffect } from "react";
import { Navigate, useLocation, Link } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../states/hooks";
import {
  loginUser,
  clearError,
  checkAuthStatus,
  selectIsAuthenticated,
  selectIsLoading,
  selectError,
} from "../../states/auth/AuthSlice";
import { useBase } from "../../context/BaseContext";
import { LockIcon, ErrorIcon, SecureLockIcon } from "../../assets/svg";
import "./Login.css";
import { LoginCredentials } from "../../model/UserData";

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isLoading = useAppSelector(selectIsLoading);
  const error = useAppSelector(selectError);

  const { showLoading, hideLoading, showError } = useBase();
  const location = useLocation();

  // Check auth status on component mount
  // useEffect(() => {
  //   dispatch(checkAuthStatus());
  // }, [dispatch]);

  // Clear error when component unmounts or when user starts typing
  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  // Redirect to intended destination or dashboard if already authenticated
  if (isAuthenticated && !isLoading) {
    const from = (location.state as any)?.from?.pathname || "/";
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Basic validation
    if (!email || !password) {
      showError("Validation Error", "Please fill in all fields");
      setIsSubmitting(false);
      return;
    }

    // if (!/\S+@\S+\.\S+/.test(email)) {
    //   showError("Validation Error", "Please enter a valid email address");
    //   setIsSubmitting(false);
    //   return;
    // }

    // Show loading during login attempt
    showLoading("Signing you in...");

    const credentials: LoginCredentials = { email, password };

    try {
      const result = await dispatch(loginUser(credentials));

      if (loginUser.fulfilled.match(result)) {
        // Login successful - redirect to intended destination
        const from = (location.state as any)?.from?.pathname || "/";
        window.location.href = from;
      } else if (loginUser.rejected.match(result)) {
        // Login failed
        showError("Login Failed", result.payload || "Invalid credentials");
      }
    } catch (error) {
      showError("Login Error", "An unexpected error occurred");
    } finally {
      hideLoading();
      setIsSubmitting(false);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (error) {
      dispatch(clearError());
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    if (error) {
      dispatch(clearError());
    }
  };

  // Show loading spinner during initial auth check
  if (isLoading && !isSubmitting) {
    return (
      <div className="login-loading">
        <div className="login-loading-content">
          <div className="login-spinner"></div>
          <p className="login-loading-text">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="login-icon-container">
            <LockIcon className="login-icon" />
          </div>
          <h2 className="login-title">Sign in to TanVan</h2>
          {/* <p className="login-subtitle">
            Or{" "}
            <Link to="/register" className="login-register-link">
              create a new account
            </Link>
          </p> */}
        </div>

        <form className="login-form" onSubmit={handleSubmit}>
          <div className="login-input-group">
            <div>
              <label htmlFor="email" className="login-input-label">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="phone"
                required
                value={email}
                onChange={handleEmailChange}
                className="login-input login-input-email"
                placeholder="Email address"
                disabled={isSubmitting}
              />
            </div>
            <div>
              <label htmlFor="password" className="login-input-label">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={handlePasswordChange}
                className="login-input login-input-password"
                placeholder="Password"
                disabled={isSubmitting}
              />
            </div>
          </div>

          {error && (
            <div className="login-error-alert" role="alert" aria-live="polite">
              <div className="login-error-content">
                <div className="login-error-icon-container">
                  <ErrorIcon className="login-error-icon" />
                </div>
                <div>
                  <h3 className="login-error-message">{error}</h3>
                </div>
              </div>
            </div>
          )}

          <div className="login-options">
            <div className="login-remember-container">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                className="login-checkbox"
              />
              <label htmlFor="remember-me" className="login-remember-label">
                Remember me
              </label>
            </div>

            {/* <div>
              <Link to="/forgot-password" className="login-forgot-link">
                Forgot your password?
              </Link>
            </div> */}
          </div>

          <div>
            <button
              type="submit"
              disabled={isSubmitting || isLoading}
              className="login-submit-button"
            >
              <span className="login-submit-icon-container">
                {isSubmitting ? (
                  <div className="login-submit-spinner"></div>
                ) : (
                  <SecureLockIcon className="login-submit-icon" />
                )}
              </span>
              {isSubmitting ? "Signing in..." : "Sign in"}
            </button>
          </div>

          {/* Demo credentials info */}
          {/* <div className="login-demo-container">
            <div className="login-demo-content">
              <div className="login-demo-icon-container">
                <svg
                  className="login-demo-icon"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="login-demo-text-container">
                <h3 className="login-demo-title">Demo Credentials</h3>
                <div className="login-demo-credentials">
                  <p>Email: admin@example.com</p>
                  <p>Password: password</p>
                </div>
              </div>
            </div>
          </div> */}
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
